"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OpBillingOpenApiOperations = void 0;
/**
 * OP Billing Workstation operations — ADR-062, frozen in Phase 4B
 * (`docs/OP-BILLING-PHASE-4B-HTTP-CONTRACT.md` §1.2) and registered
 * into the real OpenAPI document by
 * `backend/src/modules/financial/http/op-billing.openapi.ts`.
 *
 * THIS REGISTRY DESCRIBES WHAT THE SERVER ACTUALLY SERVES.
 *
 * That is the whole reason it exists, and it is why the count here does
 * not track the frozen contract's. A generated client is trusted;
 * documenting an endpoint that answers 404 would put a method on that
 * client which cannot be called, and the caller would discover it in
 * production rather than at compile time. The five unimplemented
 * operations are listed below so their absence is a recorded decision
 * rather than an oversight, and each is added here in the same commit
 * that adds its route.
 *
 * Count today: 33 — the frozen 22 that shipped in Phase 4B, `price`
 * (added 2026-08-03), the four Employee/Company Credit ledger reads that
 * arrived with the credit payment modes, `account-heads`
 * (added 2026-08-05 with the catalog's browse tabs), and the three
 * STAFF DISCOUNT operations (added 2026-08-10), `today-appointments`,
 * and `consultation-fee` (added 2026-08-14 with the doctor consultation
 * chain — the desks stopped asking for a head and an amount).
 *
 * NOT YET IMPLEMENTED — deliberately absent, not forgotten (#8 `price` was
 * implemented on 2026-08-03, when the workstation needed a pre-post breakdown):
 *
 *   contract #14  POST /bills/{invoiceId}/cancel    Phase 4C.9
 *   contract #15  POST /bills/{invoiceId}/reissue   Phase 4C.10
 *   contract #16  POST /bills/{invoiceId}/refund    Phase 5 (contract only, by design)
 *
 * #17 and #18 WERE on that list — the approval workstation, "unscheduled" —
 * and were implemented on 2026-08-31 at the desk's request. They are split
 * three ways rather than the contract's two: a GET queue, an approve and a
 * reject. `decide` as one endpoint taking a verdict would make "approve" and
 * "reject" the same call with different bodies, and they carry different
 * rules — a rejection REQUIRES a reason, an approval may sanction less than
 * was asked for.
 *
 * TWO DIVERGENCES FROM THE FROZEN CONTRACT, recorded rather than silently
 * reconciled — closing them is a contract decision, not a refactor:
 *
 *   1. Contract #19 freezes `GET /patients/{patientId}/billing-context`.
 *      The implementation serves `GET /billing-context?branchId&counterId`.
 *      The implemented shape is the correct one: the context describes the
 *      COUNTER and CASHIER, not the patient — it is loaded once per session,
 *      before any patient is chosen (Billing Context Rule 2 and Rule 3).
 *      Keying it by patient would make it un-cacheable for the session and
 *      would imply it carries patient state, which Rule 2 forbids.
 *   2. `GET /bills/{invoiceId}/print-history` is implemented but appears
 *      nowhere in the frozen list. It is registered here because it is real.
 *
 * `responseSchemaRef` names the projection DTO each endpoint returns.
 * Bodies are documented loosely at registration time (`envelope(unknown)`),
 * matching the precedent set by Financial, Patient and Clinical — request
 * bodies use the precise Zod schemas that already gate the router, so a
 * client's INPUT types are exact even where its output types are open.
 */
exports.OpBillingOpenApiOperations = [
    // -------------------------------------------------------------------------
    // Draft lifecycle — all on `financial:op-bill:create`. A draft carries no
    // number, no GL and no AR, so the authority boundary is "may this person
    // bill at all", not one permission per lifecycle verb.
    // -------------------------------------------------------------------------
    {
        operationId: 'createOpBillDraft',
        method: 'POST',
        path: '/v1/op-billing/drafts',
        summary: 'Open a new OP bill draft at a counter',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'CreateOpBillDraftRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'listOpBillDrafts',
        method: 'GET',
        path: '/v1/op-billing/drafts',
        summary: 'List open and held drafts, optionally scoped to one counter',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'ListOpBillDraftsQuery',
        responseSchemaRef: 'OpBillDraftListResponse',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillDraft',
        method: 'GET',
        path: '/v1/op-billing/drafts/{draftId}',
        summary: 'Fetch one draft with its priced lines and current lock version',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: '',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'updateOpBillDraft',
        method: 'PUT',
        path: '/v1/op-billing/drafts/{draftId}',
        summary: 'Replace a draft’s lines (CAS-guarded by expectedLockVersion)',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'UpdateOpBillDraftRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'holdOpBillDraft',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/hold',
        summary: 'Park a draft with a reason; held drafts block their counter’s cash session from closing',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'HoldOpBillDraftRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'setOpBillDraftDiscount',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/discount',
        summary: 'Set the bill-level discount on a draft; the policy decides whether it applies now or needs approval',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'RequestBillDiscountRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    /**
     * THE APPROVER'S SIDE — contracts #17/#18, implemented 2026-08-31.
     *
     * The service could always approve and reject; no route reached it, so a
     * discount above the policy ceiling stayed `REQUESTED` for ever, never
     * applied, and blocked its own bill from posting.
     */
    {
        operationId: 'listOpBillDiscountApprovals',
        method: 'GET',
        path: '/v1/op-billing/discount-approvals',
        summary: 'The pending bill-discount queue, joined to the patient and bill total the approver needs',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'EmptyRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'approveOpBillDiscount',
        method: 'POST',
        path: '/v1/op-billing/discount-approvals/{approvalId}/approve',
        summary: 'Approve a pending bill discount, optionally for less than was requested',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'ApproveBillDiscountRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'rejectOpBillDiscount',
        method: 'POST',
        path: '/v1/op-billing/discount-approvals/{approvalId}/reject',
        summary: 'Refuse a pending bill discount; a reason is required',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'RejectBillDiscountRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'clearOpBillDraftDiscount',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/discount/clear',
        summary: 'Withdraw the bill-level discount on a draft',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        // No body: the draft id in the path says everything this needs.
        requestSchemaRef: 'EmptyRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    /**
     * STAFF DISCOUNT — ADR: an admin-set concession on one account head, given by
     * naming the employee it is for.
     *
     * Note what the apply request does NOT carry: an amount. The rule the
     * administrator set decides it, which is what makes the figure non-editable
     * at the counter — there is no field on the wire to tamper with.
     */
    {
        operationId: 'getStaffDiscountOffer',
        method: 'GET',
        path: '/v1/op-billing/staff-discount/account-heads/{accountHeadId}',
        summary: 'The staff discount rule in force for an account head, if any',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'EmptyRequest',
        responseSchemaRef: 'StaffDiscountOfferResponse',
        errorCodes: [],
    },
    {
        operationId: 'applyStaffDiscount',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/staff-discount',
        summary: 'Give the staff discount on a draft to a named employee; the rule sets the amount',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'ApplyStaffDiscountRequest',
        responseSchemaRef: 'StaffDiscountAppliedResponse',
        errorCodes: [],
    },
    {
        operationId: 'clearStaffDiscount',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/staff-discount/clear',
        summary: 'Withdraw the staff discount from a draft',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'EmptyRequest',
        responseSchemaRef: 'StaffDiscountAppliedResponse',
        errorCodes: [],
    },
    {
        operationId: 'resumeOpBillDraft',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/resume',
        summary: 'Return a held draft to OPEN',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'ResumeOpBillDraftRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'discardOpBillDraft',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/discard',
        summary: 'Abandon a draft (state transition, not deletion — the row is retained for audit)',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'DiscardOpBillDraftRequest',
        responseSchemaRef: 'OpBillDraftResponse',
        errorCodes: [],
    },
    {
        operationId: 'priceOpBillDraft',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/price',
        summary: 'Price a draft: per-line rate, discount, tax and net, plus the bill total. Persists nothing',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: '',
        responseSchemaRef: 'PriceBillResponse',
        errorCodes: [],
    },
    {
        operationId: 'priceOpBillPreview',
        method: 'POST',
        path: '/v1/op-billing/price-preview',
        summary: 'Price lines for a patient or walk-in without saving a draft: per-line rate, tax and net, plus the bill total. Persists nothing',
        version: 'v1',
        tags: ['OP Billing — Drafts'],
        requestSchemaRef: 'PriceOpBillPreviewRequest',
        responseSchemaRef: 'PriceBillResponse',
        errorCodes: [],
    },
    // -------------------------------------------------------------------------
    // Post — the one command that allocates a statutory invoice number. Gated on
    // `receivable:invoice:create`, NOT the draft permission: preparing a bill and
    // committing a legal financial document are different authorities.
    // -------------------------------------------------------------------------
    {
        operationId: 'postOpBill',
        method: 'POST',
        path: '/v1/op-billing/drafts/{draftId}/post',
        summary: 'Post a draft: allocate the invoice number, price, tax, post to GL and AR, optionally collect',
        version: 'v1',
        tags: ['OP Billing — Bills'],
        requestSchemaRef: 'PostOpBillRequest',
        responseSchemaRef: 'PostBillResponse',
        errorCodes: [],
    },
    // -------------------------------------------------------------------------
    // Post-post commands. These address an IMMUTABLE aggregate (ADR-065), which
    // is why they live under `/bills/*` rather than sharing `/drafts/*`.
    // -------------------------------------------------------------------------
    {
        operationId: 'collectOpBillPayment',
        method: 'POST',
        path: '/v1/op-billing/bills/{invoiceId}/collect-payment',
        summary: 'Collect payment against a posted invoice (split across up to 10 modes)',
        version: 'v1',
        tags: ['OP Billing — Bills'],
        requestSchemaRef: 'CollectOpBillPaymentRequest',
        responseSchemaRef: 'CollectPaymentResponse',
        errorCodes: [],
    },
    {
        operationId: 'printOpBill',
        method: 'POST',
        path: '/v1/op-billing/bills/{invoiceId}/print',
        summary: 'Render a bill, receipt, estimate or duplicate; every print is its own audit row',
        version: 'v1',
        tags: ['OP Billing — Bills'],
        requestSchemaRef: 'PrintOpBillRequest',
        responseSchemaRef: 'PrintBillResponse',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillPrintHistory',
        method: 'GET',
        path: '/v1/op-billing/bills/{invoiceId}/print-history',
        summary: 'Every print of one invoice, with who printed it, when, and why for duplicates',
        version: 'v1',
        tags: ['OP Billing — Bills'],
        requestSchemaRef: '',
        responseSchemaRef: 'PrintHistoryResponse',
        errorCodes: [],
    },
    // -------------------------------------------------------------------------
    // Register — READ ONLY, on `receivable:ledger:read`. An auditor needs this
    // without ever holding the authority to create or post a bill.
    // -------------------------------------------------------------------------
    {
        operationId: 'listOpBills',
        method: 'GET',
        path: '/v1/op-billing/bills',
        summary: 'Bill register: filter, sort and page by composite cursor (invoiceDate, id)',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'ListOpBillsQuery',
        responseSchemaRef: 'BillRegisterPage',
        errorCodes: [],
    },
    {
        operationId: 'getOpBill',
        method: 'GET',
        path: '/v1/op-billing/bills/{invoiceId}',
        summary: 'One posted bill with its snapshot lines, payments and refund state',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: '',
        responseSchemaRef: 'BillDetailResponse',
        errorCodes: [],
    },
    // -------------------------------------------------------------------------
    // Dashboard — seven endpoints, one per card (Rule 2), so each gets its own
    // caching, permission granularity, SLA and observability.
    // -------------------------------------------------------------------------
    {
        operationId: 'getOpBillDashboardKpis',
        method: 'GET',
        path: '/v1/op-billing/dashboard/kpis',
        summary: 'Frozen KPI set for a day. Revenue (billed) and Collected are separate figures, never conflated',
        version: 'v1',
        tags: ['OP Billing — Dashboard'],
        requestSchemaRef: 'OpBillDashboardQuery',
        responseSchemaRef: 'DashboardKpis',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillRevenueTrend',
        method: 'GET',
        path: '/v1/op-billing/dashboard/revenue-trend',
        summary: 'Billed revenue per day over a bounded window',
        version: 'v1',
        tags: ['OP Billing — Dashboard'],
        requestSchemaRef: 'OpBillDashboardQuery',
        responseSchemaRef: 'DashboardRevenueTrend',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillPaymentMix',
        method: 'GET',
        path: '/v1/op-billing/dashboard/payment-mix',
        summary: 'Collections grouped by payment mode; the group total equals Collected',
        version: 'v1',
        tags: ['OP Billing — Dashboard'],
        requestSchemaRef: 'OpBillDashboardQuery',
        responseSchemaRef: 'DashboardPaymentMix',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillHourly',
        method: 'GET',
        path: '/v1/op-billing/dashboard/hourly',
        summary: 'Bills per hour of a day — the counter-staffing view',
        version: 'v1',
        tags: ['OP Billing — Dashboard'],
        requestSchemaRef: 'OpBillDashboardQuery',
        responseSchemaRef: 'DashboardHourly',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillTopServices',
        method: 'GET',
        path: '/v1/op-billing/dashboard/top-services',
        summary: 'Most-billed services by revenue, read from invoice-line snapshots',
        version: 'v1',
        tags: ['OP Billing — Dashboard'],
        requestSchemaRef: 'OpBillDashboardQuery',
        responseSchemaRef: 'DashboardTopServices',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillDepartmentRevenue',
        method: 'GET',
        path: '/v1/op-billing/dashboard/department-revenue',
        summary: 'Billed revenue grouped by department',
        version: 'v1',
        tags: ['OP Billing — Dashboard'],
        requestSchemaRef: 'OpBillDashboardQuery',
        responseSchemaRef: 'DashboardDepartmentRevenue',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillDoctorRevenue',
        method: 'GET',
        path: '/v1/op-billing/dashboard/doctor-revenue',
        summary: 'Billed revenue grouped by doctor (joined via Doctor.userId — doctorId references User)',
        version: 'v1',
        tags: ['OP Billing — Dashboard'],
        requestSchemaRef: 'OpBillDashboardQuery',
        responseSchemaRef: 'DashboardDoctorRevenue',
        errorCodes: [],
    },
    // -------------------------------------------------------------------------
    // Session configuration and discoverability.
    // -------------------------------------------------------------------------
    {
        operationId: 'getOpBillingContext',
        method: 'GET',
        path: '/v1/op-billing/billing-context',
        summary: 'The workstation’s first call: cashier, counter, cash session, period, modes, permissions and policies in ONE round trip',
        version: 'v1',
        tags: ['OP Billing — Context'],
        requestSchemaRef: 'OpBillBillingContextQuery',
        responseSchemaRef: 'BillingContext',
        errorCodes: [],
    },
    {
        /*
          THE CALLER'S OWN DESK, when a role assigns them one — 2026-09-07.
    
          Separate from the billing context above rather than a field on it, because
          the consumer is the SIGN-IN GATE, which runs before any billing surface
          and before a branch or counter is known. Folding it into the context would
          make the gate depend on a call that itself needs the answer the gate is
          trying to produce.
    
          TAKES NO PARAMETERS. The answer is a property of who is calling; a
          `?userId=` would let one cashier read another's assignment.
        */
        operationId: 'getOpBillingAssignedCounter',
        method: 'GET',
        path: '/v1/op-billing/assigned-counter',
        summary: 'The cash counter this caller’s role assigns them to, so the sign-in gate need not ask',
        version: 'v1',
        tags: ['OP Billing — Context'],
        requestSchemaRef: 'None',
        responseSchemaRef: 'AssignedCounter',
        errorCodes: [],
    },
    // -------------------------------------------------------------------------
    // Employee / Company Credit ledgers. `receivable:ledger:read` — these ARE
    // receivable ledgers, filtered to one party, so they carry the Register's
    // authority rather than a billing one.
    // -------------------------------------------------------------------------
    {
        operationId: 'listOpBillEmployeeCreditBalances',
        method: 'GET',
        path: '/v1/op-billing/credit-ledger/employees/balances',
        summary: 'Every employee with OP bills posted to Employee Credit, and what each still owes',
        version: 'v1',
        tags: ['OP Billing — Credit Ledger'],
        requestSchemaRef: 'None',
        responseSchemaRef: 'OpBillCreditBalances',
        errorCodes: [],
    },
    {
        operationId: 'listOpBillCompanyCreditBalances',
        method: 'GET',
        path: '/v1/op-billing/credit-ledger/companies/balances',
        summary: 'Every corporate account with OP bills posted to Company Credit, and what each still owes',
        version: 'v1',
        tags: ['OP Billing — Credit Ledger'],
        requestSchemaRef: 'None',
        responseSchemaRef: 'OpBillCreditBalances',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillEmployeeCreditLedger',
        method: 'GET',
        path: '/v1/op-billing/credit-ledger/employees/{employeeId}',
        summary: 'One employee’s OP credit bills with a running outstanding balance',
        version: 'v1',
        tags: ['OP Billing — Credit Ledger'],
        requestSchemaRef: 'None',
        responseSchemaRef: 'OpBillCreditLedger',
        errorCodes: [],
    },
    {
        operationId: 'getOpBillCompanyCreditLedger',
        method: 'GET',
        path: '/v1/op-billing/credit-ledger/companies/{payerId}',
        summary: 'One corporate account’s OP credit bills with a running outstanding balance',
        version: 'v1',
        tags: ['OP Billing — Credit Ledger'],
        requestSchemaRef: 'None',
        responseSchemaRef: 'OpBillCreditLedger',
        errorCodes: [],
    },
    {
        operationId: 'searchOpBillCharges',
        method: 'GET',
        path: '/v1/op-billing/charges/search',
        summary: 'Find a charge by code, name or barcode. Returns NO price — pricing needs a payer, package and date',
        version: 'v1',
        tags: ['OP Billing — Context'],
        requestSchemaRef: 'OpBillChargeSearchQuery',
        responseSchemaRef: 'ChargeSearchResults',
        errorCodes: [],
    },
    {
        // The catalog's Account Heads tab. Reads the same table as Administration's
        // `/account-heads` but under the BILLING permission — a cashier has no
        // reason to hold a master-data permission, and pointing this screen at the
        // Administration route would 403 exactly the people who need it.
        operationId: 'listOpBillAccountHeads',
        method: 'GET',
        path: '/v1/op-billing/account-heads',
        summary: 'Account heads for the service catalog, each with its billable-service count. Returns NO price — a head is a classification, not a priced thing',
        version: 'v1',
        tags: ['OP Billing — Context'],
        requestSchemaRef: 'None',
        responseSchemaRef: 'OpBillAccountHeads',
        errorCodes: [],
    },
    {
        /**
         * WHAT A DOCTOR'S CONSULTATION COSTS — head, amount and the doctor/hospital
         * split, already decided.
         *
         * The appointment desks DISPLAY this; they no longer ask for any of it.
         * Both used to make the clerk pick an account head out of 774 rows and type
         * an amount, because a head carries no price — two desks, two chances to
         * disagree about the same doctor, with `CONS FEE - IP` one row from the OP
         * head in the same list.
         *
         * `{doctorUserId}` is a USER id, and the name says so. `Appointment.doctorId`
         * is a foreign key to User, not to the Doctor master; passing the master id
         * matches nothing and would read as "no fee configured".
         *
         * The SPLIT is computed here rather than in the browser because the same
         * two figures become the revenue leg and the doctor's payable leg — a
         * client that rounded differently would post a journal that does not add up
         * to the invoice.
         */
        operationId: 'resolveConsultationFee',
        method: 'GET',
        path: '/v1/op-billing/consultation-fee/{doctorUserId}',
        summary: "A doctor's configured consultation head, fee and doctor/hospital split",
        version: 'v1',
        tags: ['OP Billing — Context'],
        requestSchemaRef: 'None',
        responseSchemaRef: 'ConsultationFeeResolution',
        errorCodes: [],
    },
    {
        /**
         * THE CONCESSION TAB'S PREVIEW — desk, 2026-09-17. Same shape as
         * `getStaffDiscountOffer` above (a nullable `StaffDiscountOfferDto`), but
         * asked of a DOCTOR's own consultation rather than an account head — the
         * only way to single out the one doctor (Casualty's C.M.O.) who is
         * waived in full. See `StaffDiscountService.findConsultationOffer`.
         */
        operationId: 'getConsultationConcessionOffer',
        method: 'GET',
        path: '/v1/op-billing/consultation-fee/{doctorUserId}/concession-offer',
        summary: "The concession this doctor's own consultation would carry, for a given beneficiary category",
        version: 'v1',
        tags: ['OP Billing — Context'],
        requestSchemaRef: 'ConsultationConcessionOfferQuery',
        responseSchemaRef: 'StaffDiscountOfferResponse',
        errorCodes: [],
    },
    {
        operationId: 'listOpBillTodaysAppointments',
        method: 'GET',
        path: '/v1/op-billing/patients/{patientId}/today-appointments',
        summary: "Today's appointments for a patient — what the counter recommends a doctor from",
        version: 'v1',
        tags: ['OP Billing — Context'],
        requestSchemaRef: 'None',
        responseSchemaRef: 'OpBillTodaysAppointments',
        errorCodes: [],
    },
    {
        operationId: 'listOpBillDoctors',
        method: 'GET',
        path: '/v1/op-billing/doctors',
        summary: "Doctors billable against a department — returns USER ids, which is what FinInvoiceLine.doctorId references",
        version: 'v1',
        tags: ['OP Billing — Context'],
        requestSchemaRef: 'OpBillDoctorQuery',
        responseSchemaRef: 'OpBillDoctorOptions',
        errorCodes: [],
    },
    // ---------------------------------------------------------------------------
    // OP BILLING REGISTER — ADR-084. The official OP revenue register.
    //
    // Seven read-only operations, mounted at `/v1/op-billing/register`. They carry
    // their OWN permission set (`financial:op-register:*`) rather than the billing
    // permissions above: reading the revenue book and raising a bill are different
    // authorities, and export is deliberately not implied by view.
    //
    // Every one is a GET. There is no command here and there must never be — the
    // register reports revenue, it does not alter it.
    // ---------------------------------------------------------------------------
    {
        operationId: 'getOpRegisterContext',
        method: 'GET',
        path: '/v1/op-billing/register/context',
        summary: 'Report header, this caller’s capabilities, and the fields with no reachable data on an OP bill',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterFilters',
        responseSchemaRef: 'OpRegisterContext',
        errorCodes: [],
    },
    {
        operationId: 'listOpRegisterBills',
        method: 'GET',
        path: '/v1/op-billing/register/bills',
        summary: 'The register grid in BILL or SERVICE grain. POSTED bills only — cancelled and reversed are fenced out and cannot be requested',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterQuery',
        responseSchemaRef: 'OpRegisterPage',
        errorCodes: ['VALIDATION_ERROR'],
    },
    {
        operationId: 'getOpRegisterBillDetail',
        method: 'GET',
        path: '/v1/op-billing/register/bills/{invoiceId}',
        summary: 'One posted bill expanded: services, payments, finance link and journal. A cancelled bill is a 404 here, not a row',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterBillDetailParams',
        responseSchemaRef: 'OpRegisterBillDetail',
        errorCodes: ['NOT_FOUND'],
    },
    {
        operationId: 'getOpRegisterSummary',
        method: 'GET',
        path: '/v1/op-billing/register/summary',
        summary: 'Summary cards over the WHOLE filtered set — never the loaded page, so the figures do not change when the operator scrolls',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterSummaryQuery',
        responseSchemaRef: 'OpRegisterSummary',
        errorCodes: [],
    },
    {
        operationId: 'getOpRegisterGroups',
        method: 'GET',
        path: '/v1/op-billing/register/groups',
        summary: 'Group subtotals for the chosen axis. Line-level axes need SERVICE grain and are rejected in BILL grain',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterQuery',
        responseSchemaRef: 'OpRegisterGroups',
        errorCodes: ['VALIDATION_ERROR'],
    },
    {
        operationId: 'getOpRegisterByUser',
        method: 'GET',
        path: '/v1/op-billing/register/by-user',
        summary: 'The login-wise report: what each user billed, what was collected under them by mode, and how their cash drawer settled',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterSummaryQuery',
        responseSchemaRef: 'OpRegisterByUser',
        errorCodes: [],
    },
    {
        operationId: 'getOpRegisterTenderMatrix',
        method: 'GET',
        path: '/v1/op-billing/register/tender-matrix',
        summary: 'Collections by counter, then user, across every payment method, with the bank account each non-cash tender landed in',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterSummaryQuery',
        responseSchemaRef: 'OpRegisterTenderMatrix',
        errorCodes: [],
    },
    /*
      THE CASH-DRAWER AND REFUND REPORTS (2026-08-24). Both were mounted and
      working while documented nowhere, which the mount/document parity test
      caught: an endpoint absent from the spec is absent from the generated
      client, so the only way to call it is by hand-writing the URL — exactly the
      drift the spec exists to prevent.
    */
    {
        operationId: 'getOpRegisterSessions',
        method: 'GET',
        path: '/v1/op-billing/register/sessions',
        summary: 'Cash drawers in the window: what each session opened with, took, and settled to, scope-narrowed like every other register read',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterSummaryQuery',
        responseSchemaRef: 'OpRegisterSessions',
        errorCodes: [],
    },
    {
        operationId: 'getOpRegisterRefunds',
        method: 'GET',
        path: '/v1/op-billing/register/refunds',
        summary: 'Money handed back in the window, scoped on who returned it rather than who raised the bill',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterSummaryQuery',
        responseSchemaRef: 'OpRegisterRefunds',
        errorCodes: [],
    },
    {
        operationId: 'getOpRegisterReconciliation',
        method: 'GET',
        path: '/v1/op-billing/register/reconciliation',
        summary: 'Operational revenue beside the posted GL credit for the SAME invoice population, with the difference stated exactly and no tolerance',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterFilters',
        responseSchemaRef: 'OpRegisterReconciliation',
        errorCodes: [],
    },
    {
        operationId: 'exportOpRegister',
        method: 'GET',
        path: '/v1/op-billing/register/export',
        summary: 'CSV, Excel or PDF of the whole filtered set. Refuses above the row ceiling rather than producing a truncated file',
        version: 'v1',
        tags: ['OP Billing — Register'],
        requestSchemaRef: 'OpRegisterExportQuery',
        responseSchemaRef: 'OpRegisterExportFile',
        errorCodes: ['VALIDATION_ERROR'],
    },
];
//# sourceMappingURL=op-billing.operations.js.map